export type ForNeighborsErrorKind =
  | 'validation'
  | 'forbidden'
  | 'throttled'
  | 'server'
  | 'network'
  | 'sessionExpired';

export interface ForNeighborsError {
  kind: ForNeighborsErrorKind;
}

// Shared by every hook that calls a ForNeighbours RPC operation (GetSitesWithinRadius,
// SearchAllSites, ...): session establishment (checkSession -> establishSession) fails before
// the request is even sent, for reasons unrelated to the endpoint's own status codes -- keyed on
// name rather than status so it covers every EstablishSessionError variant, not just the
// token-rejected/401 case.
export function classifyForNeighborsError(e: unknown): ForNeighborsError {
  if ((e as Error)?.name === 'EstablishSessionError') return { kind: 'sessionExpired' };
  const status = (e as { status?: number })?.status;
  if (status === 400) return { kind: 'validation' };
  // A 401 that didn't come from EstablishSessionError is the endpoint itself rejecting the
  // X-UIDH session header -- the same "please re-authenticate" remedy as a session
  // establishment failure, not a 403's "you may not do this", so it's sessionExpired too.
  if (status === 401) return { kind: 'sessionExpired' };
  if (status === 403) return { kind: 'forbidden' };
  if (status === 429) return { kind: 'throttled' };
  if (status && status >= 500) return { kind: 'server' };
  return { kind: 'network' };
}
